import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CCard, CCardBody, CCardHeader, CForm, CFormLabel, CFormInput, CFormTextarea,
  CButton, CRow, CCol, CAlert, CSpinner, CBadge,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSearch, cilPlus, cilArrowLeft, cilGift } from '@coreui/icons';
import api from '../../services/api';
import { downloadPdf } from '../../services/financeDownload';

function round2(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.round(x * 100) / 100;
}

export default function GenerateInvoicePage() {
  const navigate = useNavigate();
  const [jobRef, setJobRef] = useState('');
  const [lookup, setLookup] = useState(null);
  const [lookupErr, setLookupErr] = useState('');
  const [loadingLookup, setLoadingLookup] = useState(false);

  const [description, setDescription] = useState('');
  const [subtotal, setSubtotal] = useState('');
  const [sstRate, setSstRate] = useState('6');
  const [sstEnabled, setSstEnabled] = useState(true);

  // Coupon / FMZ Points
  const [pointsInfo, setPointsInfo] = useState(null);  // { registered, points, points_per_rm, loyalty_enabled }
  const [pointsInput, setPointsInput] = useState('');   // how many points to apply

  const [submitErr, setSubmitErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const subNum    = round2(parseFloat(subtotal) || 0);
  const rateNum   = round2(parseFloat(sstRate) || 0);
  const sstAmount = useMemo(() => round2((subNum * rateNum) / 100), [subNum, rateNum]);

  const pointsPerRm   = pointsInfo?.points_per_rm || 100;
  const discountPts   = parseInt(pointsInput, 10) || 0;
  const discountRm    = discountPts > 0 ? round2(discountPts / pointsPerRm) : 0;
  const total         = useMemo(() => round2(Math.max(0, subNum + sstAmount - discountRm)), [subNum, sstAmount, discountRm]);

  const fetchPointsInfo = useCallback(async (custid) => {
    try {
      const { data } = await api.get('/finance/customer-points', { params: { custid } });
      setPointsInfo(data);
    } catch {
      setPointsInfo(null);
    }
  }, []);

  async function handleLookup(e) {
    e.preventDefault();
    setLookupErr('');
    setLookup(null);
    setCreated(null);
    setPointsInfo(null);
    setPointsInput('');
    setLoadingLookup(true);
    try {
      const { data } = await api.get('/finance/lookup-job', { params: { q: jobRef.trim() } });
      setLookup(data);
      if (data.sst) {
        setSstEnabled(data.sst.enabled);
        setSstRate(data.sst.enabled ? String(data.sst.rate) : '0');
      }
      if (data.job?.custid) {
        await fetchPointsInfo(data.job.custid);
      }
    } catch (err) {
      setLookupErr(err.response?.data?.message || 'Lookup failed.');
    } finally {
      setLoadingLookup(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    setSubmitErr('');
    if (!lookup?.job) return;
    if (lookup.existingInvoice) { setSubmitErr('This job already has an invoice.'); return; }

    // Validate points input
    if (discountPts > 0) {
      if (discountPts % pointsPerRm !== 0) {
        setSubmitErr(`Points must be a multiple of ${pointsPerRm}.`);
        return;
      }
      if (pointsInfo && discountPts > pointsInfo.points) {
        setSubmitErr(`Customer only has ${pointsInfo.points} points.`);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        job_jid: lookup.job.jid,
        description: description.trim(),
        subtotal: subNum,
        sst_rate: rateNum,
      };
      if (discountPts > 0) payload.discount_points = discountPts;

      const { data } = await api.post('/finance/invoices', payload);
      setCreated(data.invoice);
    } catch (err) {
      setSubmitErr(err.response?.data?.message || 'Could not create invoice.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDownloadPdf() {
    if (!created?.id) return;
    try {
      await downloadPdf(`/finance/invoices/${created.id}/pdf`, `${created.invoice_no || 'invoice'}.pdf`);
    } catch {
      setSubmitErr('PDF download failed.');
    }
  }

  const showCoupon = pointsInfo?.registered && pointsInfo?.loyalty_enabled && !lookup?.existingInvoice;

  return (
    <>
      <CRow className="mb-3 align-items-center">
        <CCol><h5 className="mb-0">Generate invoice</h5></CCol>
        <CCol xs="auto">
          <CButton color="secondary" variant="outline" size="sm" component={Link} to="/finance/invoices">
            <CIcon icon={cilArrowLeft} className="me-1" /> All invoices
          </CButton>
        </CCol>
      </CRow>

      <CCard className="mb-3 border-0 shadow-sm" style={{ borderRadius: 12 }}>
        <CCardHeader className="bg-white">1. Job reference (ID or job code)</CCardHeader>
        <CCardBody>
          <CForm onSubmit={handleLookup} className="d-flex flex-wrap gap-2 align-items-end">
            <div style={{ minWidth: 220, flex: 1 }}>
              <CFormLabel>Job ID or job code</CFormLabel>
              <CFormInput
                value={jobRef}
                onChange={(e) => setJobRef(e.target.value)}
                placeholder="e.g. 1234 or 2605010001"
              />
            </div>
            <CButton color="success" type="submit" disabled={loadingLookup || !jobRef.trim()}>
              {loadingLookup ? <CSpinner size="sm" /> : <><CIcon icon={cilSearch} className="me-1" /> Lookup</>}
            </CButton>
          </CForm>
          {lookupErr && <CAlert color="warning" className="mt-3 mb-0">{lookupErr}</CAlert>}
        </CCardBody>
      </CCard>

      {lookup?.job && (
        <CCard className="mb-3 border-0 shadow-sm" style={{ borderRadius: 12 }}>
          <CCardHeader className="bg-white">2. Job & customer</CCardHeader>
          <CCardBody>
            <CRow className="gy-2 small">
              <CCol md={4}><strong>Job ID</strong><div>{lookup.job.jid}</div></CCol>
              <CCol md={4}><strong>Job code</strong><div>{lookup.job.jobcode}</div></CCol>
              <CCol md={4}><strong>Status</strong><div>{lookup.job.status}</div></CCol>
              <CCol md={4}><strong>Date</strong><div>{lookup.job.date || '—'}</div></CCol>
              <CCol md={4}><strong>Customer UID</strong><div>{lookup.job.custid}</div></CCol>
              <CCol md={12}><strong>Equipment / notes</strong><div className="text-muted">{lookup.job.jenis || '—'}</div></CCol>
              {lookup.customer && (
                <>
                  <CCol md={6}><strong>Customer name</strong><div>{lookup.customer.nama}</div></CCol>
                  <CCol md={6}><strong>Phone</strong><div>{lookup.customer.telhp || '—'}</div></CCol>
                </>
              )}
            </CRow>
            {/* FMZ Points badge */}
            {pointsInfo && (
              <div className="mt-3 d-flex align-items-center gap-2">
                <CIcon icon={cilGift} className="text-warning" />
                {pointsInfo.registered && pointsInfo.loyalty_enabled ? (
                  <span className="small">
                    FMZ Points balance: <CBadge color="warning" className="ms-1">{pointsInfo.points} pts</CBadge>
                    <span className="text-muted ms-2">({Math.floor(pointsInfo.points / pointsInfo.points_per_rm)} × RM1 discount available)</span>
                  </span>
                ) : (
                  <span className="small text-muted">Customer has no FMZ Points account.</span>
                )}
              </div>
            )}
            {lookup.existingInvoice && (
              <CAlert color="info" className="mt-3 mb-0">
                Invoice already exists:{' '}
                <Link to={`/finance/invoices/${lookup.existingInvoice.id}`}>{lookup.existingInvoice.invoice_no}</Link>
                {' '}(total RM {Number(lookup.existingInvoice.total).toFixed(2)})
              </CAlert>
            )}
          </CCardBody>
        </CCard>
      )}

      {lookup?.job && !lookup.existingInvoice && (
        <CCard className="mb-3 border-0 shadow-sm" style={{ borderRadius: 12 }}>
          <CCardHeader className="bg-white">3. Invoice lines & SST</CCardHeader>
          <CCardBody>
            <CForm onSubmit={handleCreate}>
              <CRow className="g-3">
                <CCol xs={12}>
                  <CFormLabel>Invoice detail / description</CFormLabel>
                  <CFormTextarea
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Parts, labour, scope of work…"
                  />
                </CCol>
                <CCol md={4}>
                  <CFormLabel>Amount (before SST), RM</CFormLabel>
                  <CFormInput
                    type="number" inputMode="decimal" min="0" step="0.01"
                    value={subtotal} onChange={(e) => setSubtotal(e.target.value)} required
                  />
                </CCol>
                <CCol md={4}>
                  <CFormLabel>SST rate (%)</CFormLabel>
                  {sstEnabled ? (
                    <CFormInput
                      type="number" inputMode="decimal" min="0" max="100" step="0.01"
                      value={sstRate} onChange={(e) => setSstRate(e.target.value)}
                    />
                  ) : (
                    <div className="form-control bg-light text-muted" style={{ cursor: 'not-allowed' }}>
                      0% <span className="small">(SST disabled)</span>
                    </div>
                  )}
                </CCol>
                <CCol md={4}>
                  <CFormLabel className="d-block">Calculated</CFormLabel>
                  <div className="pt-2 small">
                    <div>SST: <strong>RM {sstAmount.toFixed(2)}</strong></div>
                    {discountRm > 0 && (
                      <div className="text-success">Discount: <strong>− RM {discountRm.toFixed(2)}</strong></div>
                    )}
                    <div>Total: <strong className="text-success">RM {total.toFixed(2)}</strong></div>
                  </div>
                </CCol>

                {/* ── FMZ Points Coupon ── */}
                {showCoupon && (
                  <CCol xs={12}>
                    <hr className="my-1" />
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <CIcon icon={cilGift} className="text-warning" />
                      <span className="fw-semibold">Apply FMZ Points coupon</span>
                      <CBadge color="warning">{pointsInfo.points} pts available</CBadge>
                    </div>
                    <CRow className="g-2 align-items-end">
                      <CCol md={4}>
                        <CFormLabel className="small mb-1">Points to apply (multiples of {pointsPerRm})</CFormLabel>
                        <CFormInput
                          type="number" min="0" step={pointsPerRm}
                          value={pointsInput}
                          onChange={e => setPointsInput(e.target.value)}
                          placeholder={`e.g. ${pointsPerRm}`}
                        />
                      </CCol>
                      <CCol md={4} className="small text-muted pt-2">
                        {discountPts > 0
                          ? `${discountPts} pts = RM${discountRm.toFixed(2)} off`
                          : `${pointsPerRm} pts = RM1.00 off`}
                      </CCol>
                    </CRow>
                    <div className="text-muted small mt-1">
                      Customer will have {(pointsInfo.points - discountPts)} pts remaining after redemption.
                    </div>
                  </CCol>
                )}
              </CRow>
              {submitErr && <CAlert color="danger" className="mt-3">{submitErr}</CAlert>}
              <div className="mt-3 d-flex flex-wrap gap-2">
                <CButton color="success" type="submit" disabled={saving}>
                  {saving ? <CSpinner size="sm" /> : <><CIcon icon={cilPlus} className="me-1" /> Create invoice</>}
                </CButton>
              </div>
            </CForm>
          </CCardBody>
        </CCard>
      )}

      {created && (
        <CAlert color="success" className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <span>
            Invoice <strong>{created.invoice_no}</strong> created (ID {created.id}). Total RM {Number(created.total).toFixed(2)}.
            {Number(created.discount) > 0 && (
              <span className="ms-2 text-success">(RM {Number(created.discount).toFixed(2)} FMZ Points applied)</span>
            )}
          </span>
          <span className="d-flex flex-wrap gap-2">
            <CButton color="success" variant="outline" size="sm" onClick={handleDownloadPdf}>Download PDF</CButton>
            <CButton color="secondary" variant="outline" size="sm" onClick={() => navigate(`/finance/invoices/${created.id}`)}>View HTML</CButton>
            <CButton color="secondary" variant="outline" size="sm" onClick={() => { setCreated(null); setLookup(null); setJobRef(''); setDescription(''); setSubtotal(''); setPointsInfo(null); setPointsInput(''); }}>
              New invoice
            </CButton>
          </span>
        </CAlert>
      )}
    </>
  );
}
